import React, { useState, useEffect } from 'react';
import { X, Star, MapPin, Wifi, Car, Waves, Mountain, Crown, Award, Medal, Sparkles, ExternalLink, Shield, Clock, Users } from 'lucide-react';

const ClaraTop10Modal = ({ 
  isOpen, 
  onClose, 
  destination = 'Mallorca', 
  persona = 'family',
  checkin,
  checkout,
  adults = 2 
}) => {
  const [loading, setLoading] = useState(false);
  const [properties, setProperties] = useState([]);
  const [error, setError] = useState('');
  const [selectedPersona, setSelectedPersona] = useState(persona);
  const [searchInfo, setSearchInfo] = useState({});

  const personas = [
    { id: 'family', name: 'Family', icon: '👨‍👩‍👧‍👦', color: 'bg-blue-500' },
    { id: 'wellness', name: 'Wellness', icon: '🧘‍♀️', color: 'bg-green-500' },
    { id: 'workation', name: 'Workation', icon: '💻', color: 'bg-purple-500' },
    { id: 'adventure', name: 'Adventure', icon: '🏔️', color: 'bg-orange-500' }
  ];

  const getCertificationIcon = (level) => {
    switch(level) {
      case 'gold': return <Crown className="w-4 h-4 text-yellow-500" />;
      case 'silver': return <Award className="w-4 h-4 text-gray-400" />;
      case 'bronze': return <Medal className="w-4 h-4 text-amber-600" />;
      default: return <Shield className="w-4 h-4 text-green-500" />;
    }
  };

  const fetchProperties = async () => {
    if (!isOpen) return;
    
    setLoading(true);
    setError('');
    
    try {
      const params = new URLSearchParams({
        dest: destination,
        persona: selectedPersona,
        adults: adults.toString()
      });
      
      if (checkin) params.append('from', checkin);
      if (checkout) params.append('to', checkout);
      
      const response = await fetch(`/api/heartplaces/search?${params}`);
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Search failed');
      }
      
      setProperties(data.items || []);
      setSearchInfo({
        destination: data.destination,
        persona: data.persona,
        marketAverage: data.marketAverage,
        totalFound: data.totalFound,
        cached: data.cached
      });
      
    } catch (err) {
      console.error('Search error:', err);
      setError(err.message);
      // Use fallback data if available
      if (err.fallback) {
        setProperties(err.fallback.items || []);
      }
    } finally {
      setLoading(false);
    }
  };

  const trackClick = async (propertyId) => {
    try {
      await fetch('/api/affiliate/track', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          listingId: propertyId,
          source: 'clara_modal',
          destination,
          persona: selectedPersona
        })
      });
    } catch (err) {
      console.error('Tracking error:', err);
    }
  };

  const handleBookNow = (property) => {
    trackClick(property.id);
    window.open(property.ctaUrl, '_blank', 'noopener,noreferrer');
  };

  useEffect(() => {
    fetchProperties();
  }, [isOpen, selectedPersona, destination, checkin, checkout, adults]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden">
        
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-amber-600 rounded-full flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                Clara's Top 10
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {destination} • {personas.find(p => p.id === selectedPersona)?.name} Focus
              </p>
            </div>
          </div>
          
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Persona Selector */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex flex-wrap gap-3">
            {personas.map((p) => (
              <button
                key={p.id}
                onClick={() => setSelectedPersona(p.id)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-full font-medium transition-all ${
                  selectedPersona === p.id
                    ? `${p.color} text-white shadow-lg`
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                <span>{p.icon}</span>
                <span>{p.name}</span>
              </button>
            ))}
          </div>
          
          {searchInfo.cached && (
            <div className="mt-3 text-xs text-gray-500 dark:text-gray-400 flex items-center space-x-1">
              <Clock className="w-3 h-3" />
              <span>Cached results • Updated every 6 hours</span>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="flex items-center space-x-3 text-gray-600 dark:text-gray-300">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-amber-600"></div>
                <span>Clara is analyzing properties...</span>
              </div>
            </div>
          )}

          {error && !loading && (
            <div className="text-center py-12">
              <div className="text-red-600 dark:text-red-400 mb-2">
                Search temporarily unavailable
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                {error}
              </div>
            </div>
          )}

          {!loading && !error && properties.length === 0 && (
            <div className="text-center py-12">
              <div className="text-gray-600 dark:text-gray-300 mb-2">
                No properties found matching your criteria
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Try adjusting your dates or selecting a different focus
              </div>
            </div>
          )}

          {!loading && properties.length > 0 && (
            <>
              {/* Search Summary */}
              <div className="mb-6 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-xl">
                <div className="flex items-center justify-between text-sm">
                  <div className="text-amber-800 dark:text-amber-200">
                    <strong>{properties.length}</strong> premium properties selected from{' '}
                    <strong>{searchInfo.totalFound}</strong> available
                  </div>
                  <div className="text-amber-600 dark:text-amber-300">
                    Avg. €{searchInfo.marketAverage}/night
                  </div>
                </div>
              </div>

              {/* Properties Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {properties.map((property, index) => (
                  <div
                    key={property.id}
                    className="bg-white dark:bg-gray-700 rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 border border-gray-200 dark:border-gray-600"
                  >
                    {/* Property Image */}
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={property.image}
                        alt={property.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.src = 'https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=800';
                        }}
                      />
                      
                      {/* Rank Badge */}
                      <div className="absolute top-3 left-3 w-8 h-8 bg-amber-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
                        {index + 1}
                      </div>
                      
                      {/* Clara Score */}
                      <div className="absolute top-3 right-3 bg-green-500 text-white px-2 py-1 rounded-full text-sm font-bold">
                        {property.claraScore}/100
                      </div>
                      
                      {/* Certification Badge */}
                      {property.certification && (
                        <div className="absolute bottom-3 left-3 flex items-center space-x-1 bg-white/90 dark:bg-gray-800/90 px-2 py-1 rounded-full text-xs font-medium">
                          {getCertificationIcon(property.certification.level)}
                          <span>{property.certification.badge}</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Property Details */}
                    <div className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="font-bold text-gray-900 dark:text-white text-sm leading-tight">
                          {property.name}
                        </h3>
                        <div className="flex items-center space-x-1 text-yellow-500 ml-2">
                          <Star className="w-3 h-3 fill-current" />
                          <span className="text-xs font-medium">{property.reviewScore}</span>
                        </div>
                      </div>
                      
                      <div className="flex items-center text-gray-600 dark:text-gray-300 mb-3">
                        <MapPin className="w-3 h-3 mr-1" />
                        <span className="text-xs">{property.address}</span>
                      </div>
                      
                      {/* Persona Scores */}
                      <div className="grid grid-cols-2 gap-2 mb-3 text-xs">
                        <div className="flex justify-between">
                          <span className="text-gray-500">Family:</span>
                          <span className="font-medium">{property.claraScore}/100</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Wellness:</span>
                          <span className="font-medium">{property.reviewScore}/100</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Workation:</span>
                          <span className="font-medium">{property.claraScore}/100</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Adventure:</span>
                          <span className="font-medium">{property.reviewScore}/100</span>
                        </div>
                      </div>
                      
                      {/* Evidence */}
                      <div className="bg-gray-50 dark:bg-gray-600 rounded-lg p-2 mb-3">
                        <div className="text-xs text-gray-600 dark:text-gray-300">
                          <strong>Why Clara picked this:</strong>
                        </div>
                        <div className="text-xs text-gray-700 dark:text-gray-200 mt-1">
                          {property.evidence}
                        </div>
                      </div>
                      
                      {/* Badges */}
                      <div className="flex flex-wrap gap-1 mb-3">
                        {(property.badges || []).slice(0, 3).map((badge, i) => (
                          <span
                            key={i}
                            className="px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 text-xs rounded-full"
                          >
                            {badge}
                          </span>
                        ))}
                      </div>
                      
                      {/* Price and CTA */}
                      <div className="flex items-center justify-between">
                        <div>
                          {property.nightlyFrom && (
                            <div className="text-lg font-bold text-gray-900 dark:text-white">
                              €{property.nightlyFrom}
                            </div>
                          )}
                          <div className="text-xs text-gray-500">per night</div>
                        </div>
                        
                        <button
                          onClick={() => handleBookNow(property)}
                          className="bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-lg font-semibold text-sm transition-colors flex items-center space-x-1"
                        >
                          <span>Book Now</span>
                          <ExternalLink className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
        
        {/* Footer */}
        <div className="p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
          <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-1">
                <Shield className="w-3 h-3" />
                <span>Clara Certified Quality</span>
              </div>
              <div className="flex items-center space-x-1">
                <Users className="w-3 h-3" />
                <span>Real Guest Reviews</span>
              </div>
            </div>
            <div>
              Powered by Clara360 AI
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClaraTop10Modal;

